/**
 * DOMAIN MODEL — Event is the generic root entity that replaces
 * wedding-specific data as the primary operational record. A wedding is
 * one EventType inside one vertical ("weddings"); the same Event shape
 * will carry XV años, corporate events, conferences, etc. later without
 * schema changes — only new EventType rows and vertical-specific detail
 * tables (see wedding-details.ts) are added per vertical.
 */
export type EventStatus = "draft" | "active" | "completed" | "canceled" | "archived";

export type EventLifecycleStage =
  | "inquiry"
  | "proposal"
  | "booked"
  | "planning"
  | "production"
  | "final_review"
  | "event_day"
  | "completed"
  | "archived";

export interface Event {
  id: string;
  organizationId: string;
  eventTypeId: string;
  eventTypeKey?: string;
  title: string;
  slug?: string;
  status: EventStatus;
  lifecycleStage: EventLifecycleStage;
  startDate: string;
  endDate?: string;
  timezone: string;
  venueName?: string;
  venueAddress?: string;
  venueCity?: string;
  venueState?: string;
  venueCountry?: string;
  guestCountEstimate?: number;
  budgetEstimate?: number;
  currency: string;
  coverImageUrl?: string;
  notes?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
  suspendedAt?: string;
  deletedAt?: string;
  purgeAfter?: string;
  weddingDetails?: import("@/types/domain/wedding-details").WeddingDetails;
}

/**
 * Do not hardcode event types in components — read from EventType rows.
 * The first (and, for now, only) vertical is "weddings"; EventType is what
 * lets XV años / corporate / conferences / etc. be added later as data,
 * not code changes.
 */
export interface EventType {
  id: string;
  verticalKey: string;
  key: string;
  name: string;
  description?: string;
  icon?: string;
  isActive: boolean;
  defaultTemplateId?: string;
  createdAt: string;
  updatedAt: string;
}

export const WEDDING_EVENT_TYPE: EventType = {
  id: "evt_type_wedding",
  verticalKey: "weddings",
  key: "wedding",
  name: "Wedding",
  isActive: true,
  createdAt: "2026-01-01T00:00:00.000Z",
  updatedAt: "2026-01-01T00:00:00.000Z",
};

/** Database IDs vary by environment; identify real events by their catalog key. */
export function isWeddingEvent(event: Pick<Event, "eventTypeId" | "eventTypeKey">): boolean {
  return event.eventTypeKey === WEDDING_EVENT_TYPE.key ||
    (!event.eventTypeKey && event.eventTypeId === WEDDING_EVENT_TYPE.id);
}

export type EventMemberStatus = "invited" | "active" | "inactive" | "removed";

/**
 * Connects a user (organization member, or an external person such as a
 * client/vendor with no organization membership at all) to a specific
 * event. `organizationMemberId` is optional precisely so clients, vendors,
 * and temporary event staff — who are not members of the planning
 * organization — can still be event members.
 */
export interface EventMember {
  id: string;
  eventId: string;
  userId: string;
  organizationMemberId?: string;
  status: EventMemberStatus;
  invitedBy?: string;
  invitedAt?: string;
  joinedAt?: string;
  createdAt: string;
  updatedAt: string;
}
